import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  Alert,
  Chip,
  Checkbox,
  ListItemIcon,
  OutlinedInput,
  CircularProgress,
  Paper,
  ListItemText,
} from '@mui/material';
import {
  Receipt,
  AccountBalanceWallet
} from '@mui/icons-material';
import { Invoice } from '../../types';
import paymentService, { PaymentSummary, PaymentInvoiceCredit } from '../../services/paymentService';
import clienteCreditoService from '../../services/clienteCreditoService';
import { usePaymentHandler } from '../../hooks/usePaymentHandler';
import FormaPgtoSelect from '../selects/FormaPgtoSelect';
import invoiceService from '@/services/invoiceService';
import motivoDescontoService, { MotivoDescontoSelect } from '@/services/motivoDescontoService';
import { getLocalDateTimeString } from '@/utils/formatToView';

interface InvoicePaymentFormProps {
  open: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onSuccess?: (invoicePaymentData: InvoicePaymentData, invoice: Invoice) => void;
  onError?: (error: string) => void;
  onCancel?: () => void;
  showSuccessAlert?: boolean;
  autoCloseOnSuccess?: boolean;
  reloadDataOnSuccess?: () => Promise<void>;
}

export interface InvoicePaymentData {
  paymentMethod: string;
  transactionId?: string;
  paidAmount?: number;
  isPartialPayment?: boolean;
  notes?: string;
  paymentDate?: string;
  selectedCredits?: number[];
  creditDiscount?: number;
  excessAmount?: number;
  shouldCreateCredit?: boolean;
  creditType?: string;
  creditDescription?: string;
  valor_acrescimo?: number;
  desconto?: number;
  motivoDescontoId?: number;
}

const InvoicePaymentForm: React.FC<InvoicePaymentFormProps> = ({
  open,
  onClose,
  invoice,
  onSuccess,
  onError,
  onCancel,
  showSuccessAlert = true,
  autoCloseOnSuccess = true,
  reloadDataOnSuccess
}) => {
  const { paymentLoading, handleReceivePayment } = usePaymentHandler();

  const [invoicePaymentData, setInvoicePaymentData] = useState<InvoicePaymentData>({
    paymentMethod: '',
    transactionId: '',
    paidAmount: 0,
    isPartialPayment: false,
    notes: '',
    paymentDate: getLocalDateTimeString(),
    selectedCredits: [],
    creditDiscount: 0,
    excessAmount: 0,
    shouldCreateCredit: false,
    creditType: 'pagamento_excedente',
    creditDescription: 'Crédito gerado por pagamento excedente',
    valor_acrescimo: 0,
    desconto: 0,
    motivoDescontoId: undefined
  });
  const [motivosDesconto, setMotivosDesconto] = useState<MotivoDescontoSelect[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [modalData, setModalData] = useState<{
    invoice: { id: number; cliente_id: number; valor: number; acrescimo: number; desconto: number; status: string; descricao: string; data_vencimento: string; data_emissao?: string; valor_multa: number; valor_juros: number; dias_atraso: number };
    paymentSummary: PaymentSummary;
    availableCredits: PaymentInvoiceCredit[];
    descontoAntecipadoConfig?: {
      tipo: 'PERCENTAGE' | 'FIXED' | '';
      valor: number;
      dias: number;
    };
  } | null>(null);
  const [loadingModal, setLoadingModal] = useState(false);

  const calculateDescontoAntecipado = (
    invoiceData: { valor: number; data_vencimento: string },
    paymentDate: string,
    config?: { tipo: 'PERCENTAGE' | 'FIXED' | ''; valor: number; dias: number }
  ): number => {
    if (!config || !config.tipo || config.valor <= 0 || !invoiceData?.data_vencimento || !paymentDate) {
      return 0;
    }

    const vencStr = String(invoiceData.data_vencimento).split('T')[0];
    const vencParts = vencStr.split('-').map(Number);
    if (vencParts.length !== 3 || vencParts.some(isNaN)) return 0;
    const dataVenc = new Date(vencParts[0], vencParts[1] - 1, vencParts[2]);

    const payStr = String(paymentDate).split('T')[0];
    const payParts = payStr.split('-').map(Number);
    if (payParts.length !== 3 || payParts.some(isNaN)) return 0;
    const dataPagto = new Date(payParts[0], payParts[1] - 1, payParts[2]);

    const dataLimite = new Date(dataVenc);
    dataLimite.setDate(dataLimite.getDate() - (config.dias || 0));

    if (dataPagto <= dataLimite) {
      let desconto = 0;
      if (config.tipo === 'PERCENTAGE') {
        desconto = Number(invoiceData.valor) * (config.valor / 100);
      } else if (config.tipo === 'FIXED') {
        desconto = config.valor;
      }
      return Math.min(Number(invoiceData.valor), Math.round(desconto * 100) / 100);
    }

    return 0;
  };

  useEffect(() => {
    motivoDescontoService.getMotivosParaSelect().then(list => {
      setMotivosDesconto(list);
    }).catch(() => setMotivosDesconto([]));
  }, []);

  useEffect(() => {
    const loadPaymentInvoiceData = async () => {
      if (!invoice || !open) {
        setModalData(null);
        return;
      }

      try {
        setLoadingModal(true);
        const [response, motivosList] = await Promise.all([
          paymentService.getPaymentInvoiceData(invoice.id),
          motivoDescontoService.getMotivosParaSelect().catch(() => [])
        ]);

        if (motivosList && motivosList.length > 0) {
          setMotivosDesconto(motivosList);
        }

        if (response.success && response.data) {
          const { invoice: invoiceData, paymentSummary, availableCredits, descontoAntecipadoConfig } = response.data;
          setModalData({ invoice: invoiceData, paymentSummary, availableCredits, descontoAntecipadoConfig });
          const currentDate = getLocalDateTimeString();
          const autoDesconto = calculateDescontoAntecipado(invoiceData, currentDate, descontoAntecipadoConfig);

          let autoMotivoId: number | undefined = undefined;
          const listToUse = (motivosList && motivosList.length > 0) ? motivosList : motivosDesconto;

          if (autoDesconto > 0 && listToUse.length > 0) {
            const defaultMotivo = listToUse.find(m => m.is_default);
            const motivoAntecipacao = defaultMotivo || listToUse.find(m =>
              m.label.toLowerCase().includes('antecip') || m.label.toLowerCase().includes('pontual')
            );
            if (motivoAntecipacao) {
              autoMotivoId = motivoAntecipacao.value;
            } else {
              autoMotivoId = listToUse[0]?.value;
            }
          }

          const valorMulta = invoiceData.valor_multa ?? 0;
          const valorJuros = invoiceData.valor_juros ?? 0;
          const baseRemaining = paymentSummary.remainingAmount;
          const valorAReceberComJurosMulta = baseRemaining + valorMulta + valorJuros;
          const valorAcrescimo = valorMulta + valorJuros;
          const valorFinalAReceber = Math.max(0, valorAReceberComJurosMulta - autoDesconto);

          setInvoicePaymentData(prev => ({
            ...prev,
            paymentMethod: '',
            transactionId: '',
            paidAmount: valorFinalAReceber,
            valor_acrescimo: valorAcrescimo,
            desconto: autoDesconto,
            motivoDescontoId: autoMotivoId,
            isPartialPayment: false,
            notes: '',
            paymentDate: currentDate
          }));
          setErrors([]);
        } else {
          throw new Error('Resposta inválida da API');
        }
      } catch (error) {
        console.error('Erro ao carregar dados do modal de pagamento:', error);
        setModalData(null);
        onError?.(error instanceof Error ? error.message : 'Erro ao carregar dados');
        const totalAmount = invoice ? (Number(invoice.valor) + Number(invoice.acrescimo || 0) - Number(invoice.desconto || 0)) : 0;
        setInvoicePaymentData(prev => ({
          ...prev,
          paidAmount: totalAmount,
          paymentDate: getLocalDateTimeString()
        }));
      } finally {
        setLoadingModal(false);
      }
    };

    loadPaymentInvoiceData();
  }, [invoice?.id, open]);

  const calculateTotal = (inv: { valor: number; acrescimo?: number; desconto?: number }) => {
    const valor = Number(inv.valor) || 0;
    const acrescimo = Number(inv.acrescimo) || 0;
    const desconto = Number(inv.desconto) || 0;
    return valor + acrescimo - desconto;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatCurrencyInput = (value: string): string => {
    const numericValue = value.replace(/\D/g, '');
    if (!numericValue) return '';
    const cents = parseInt(numericValue);
    const reais = cents / 100;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(reais);
  };

  const parseCurrencyInput = (formattedValue: string): number => {
    const numericString = formattedValue
      .replace(/[R$\s.]/g, '')
      .replace(',', '.');
    return parseFloat(numericString) || 0;
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const formattedValue = formatCurrencyInput(inputValue);
    const numericValue = parseCurrencyInput(formattedValue);

    const valorMulta = modalData && 'valor_multa' in modalData.invoice ? modalData.invoice.valor_multa ?? 0 : 0;
    const valorJuros = modalData && 'valor_juros' in modalData.invoice ? modalData.invoice.valor_juros ?? 0 : 0;
    const baseRemaining = modalData ? modalData.paymentSummary.remainingAmount : (invoice ? calculateTotal(invoice) : 0);
    const desconto = invoicePaymentData.desconto ?? 0;
    const currentRemaining = baseRemaining + valorMulta + valorJuros - (invoicePaymentData.creditDiscount ?? 0) - desconto;
    const excessAmount = Math.max(0, numericValue - currentRemaining);
    const shouldCreateCredit = excessAmount > 0;

    setInvoicePaymentData(prev => ({
      ...prev,
      paidAmount: numericValue,
      excessAmount,
      shouldCreateCredit,
      creditDescription: shouldCreateCredit
        ? `Crédito gerado por pagamento excedente de ${formatCurrency(excessAmount)}`
        : 'Crédito gerado por pagamento excedente'
    }));
  };

  const getDisplayValue = (): string => {
    if (!invoicePaymentData.paidAmount || invoicePaymentData.paidAmount === 0) {
      return '';
    }
    return formatCurrency(invoicePaymentData.paidAmount);
  };

  const handleDescontoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const formattedValue = formatCurrencyInput(inputValue);
    const numericValue = parseCurrencyInput(formattedValue);
    const valorMulta = modalData && 'valor_multa' in modalData.invoice ? modalData.invoice.valor_multa ?? 0 : 0;
    const valorJuros = modalData && 'valor_juros' in modalData.invoice ? modalData.invoice.valor_juros ?? 0 : 0;
    const baseRemaining = modalData ? modalData.paymentSummary.remainingAmount : (invoice ? calculateTotal(invoice) : 0);
    const valorAReceberComJurosMulta = baseRemaining + valorMulta + valorJuros;
    const newAmountToPay = Math.max(0, valorAReceberComJurosMulta - (invoicePaymentData.creditDiscount ?? 0) - numericValue);

    setInvoicePaymentData(prev => ({
      ...prev,
      desconto: numericValue,
      motivoDescontoId: numericValue === 0 ? undefined : prev.motivoDescontoId,
      paidAmount: newAmountToPay
    }));
  };

  const getDescontoDisplayValue = (): string => {
    if (!invoicePaymentData.desconto || invoicePaymentData.desconto === 0) {
      return '';
    }
    return formatCurrency(invoicePaymentData.desconto);
  };

  const handleCreditSelection = (creditIds: number[]) => {
    const credits = modalData?.availableCredits ?? [];
    const selectedCreditObjects = credits.filter(credit =>
      creditIds.includes(credit.id)
    );

    const totalCreditDiscount = selectedCreditObjects.reduce((sum, credit) => {
      const saldoNumerico = Number(credit.saldo) || 0;
      return sum + saldoNumerico;
    }, 0);

    const valorMulta = modalData && 'valor_multa' in modalData.invoice ? modalData.invoice.valor_multa ?? 0 : 0;
    const valorJuros = modalData && 'valor_juros' in modalData.invoice ? modalData.invoice.valor_juros ?? 0 : 0;
    const baseRemaining = Number(modalData ? modalData.paymentSummary.remainingAmount : (invoice ? calculateTotal(invoice) : 0)) || 0;
    const valorAReceberComJurosMulta = baseRemaining + valorMulta + valorJuros;
    const desconto = invoicePaymentData.desconto ?? 0;
    const newAmountToPay = Math.max(0, valorAReceberComJurosMulta - totalCreditDiscount - desconto);

    setInvoicePaymentData(prev => ({
      ...prev,
      selectedCredits: creditIds,
      creditDiscount: totalCreditDiscount,
      paidAmount: newAmountToPay
    }));
  };

  const formatCreditOption = (credit: PaymentInvoiceCredit): string => {
    const date = new Date(credit.createdAt).toLocaleDateString('pt-BR');
    const origem = credit.cliente_origem?.nome || 'Sistema';
    return `${formatCurrency(credit.saldo)} - ${origem} (${date})`;
  };

  const validatePayment = (): boolean => {
    const newErrors: string[] = [];

    if (!invoicePaymentData.paymentMethod) {
      newErrors.push('Método de pagamento é obrigatório');
    }

    if (!invoicePaymentData.paidAmount || invoicePaymentData.paidAmount <= 0) {
      newErrors.push('Valor pago deve ser maior que zero');
    }

    if (!invoicePaymentData.paymentDate) {
      newErrors.push('Data do pagamento é obrigatória');
    }

    const desconto = invoicePaymentData.desconto ?? 0;
    if (desconto > 0 && !invoicePaymentData.motivoDescontoId) {
      newErrors.push('Motivo de desconto é obrigatório quando há desconto aplicado');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const processPayment = async (): Promise<void> => {
    const invoiceToUse = modalData?.invoice ?? invoice;
    if (!invoiceToUse) {
      const errorMsg = 'Erro: Nenhuma fatura selecionada para pagamento';
      console.error(errorMsg);
      onError?.(errorMsg);
      return;
    }
    try {
      await handleReceivePayment(invoicePaymentData, invoiceToUse as Invoice, {
        showAlert: (type, message) => {
          if (type === 'success') {
            onSuccess?.(invoicePaymentData, invoiceToUse as Invoice);
            if (reloadDataOnSuccess) {
              reloadDataOnSuccess();
            }
            if (autoCloseOnSuccess) {
              handleClose();
            }
          } else {
            console.error('❌ Erro no pagamento:', message);
            onError?.(message);
          }
        },
        loadInvoices: async () => {},
        closeDialog: () => {},
        resetSelectedInvoice: () => {}
      });
    } catch (error: any) {
      const errorMsg = error.message || 'Erro ao processar pagamento';
      console.error('💥 Erro capturado:', errorMsg);
      onError?.(errorMsg);
    }
  };

  const handleConfirm = async () => {
    if (validatePayment()) {
      await processPayment();
    }
  };

  const handleClose = () => {
    setInvoicePaymentData({
      paymentMethod: '',
      transactionId: '',
      paidAmount: 0,
      isPartialPayment: false,
      notes: '',
      paymentDate: new Date().toISOString().split('T')[0],
      selectedCredits: [],
      creditDiscount: 0,
      excessAmount: 0,
      shouldCreateCredit: false,
      creditType: 'pagamento_excedente',
      creditDescription: 'Crédito gerado por pagamento excedente',
      valor_acrescimo: 0,
      desconto: 0,
      motivoDescontoId: undefined
    });
    setErrors([]);
    setModalData(null);
    onCancel?.();
    onClose();
  };

  const displayInvoice = modalData?.invoice ?? invoice;
  const totalAmount = modalData ? modalData.paymentSummary.totalAmount : (invoice ? calculateTotal(invoice) : 0);
  const remainingAmount = modalData ? modalData.paymentSummary.remainingAmount : totalAmount;
  const totalPaid = modalData ? modalData.paymentSummary.totalPaid : 0;
  const valorMulta = displayInvoice && 'valor_multa' in displayInvoice ? (displayInvoice as { valor_multa?: number }).valor_multa ?? 0 : 0;
  const valorJuros = displayInvoice && 'valor_juros' in displayInvoice ? (displayInvoice as { valor_juros?: number }).valor_juros ?? 0 : 0;
  const diasAtraso = displayInvoice && 'dias_atraso' in displayInvoice ? (displayInvoice as { dias_atraso?: number }).dias_atraso ?? 0 : 0;
  const valorAReceberComJurosMulta = remainingAmount + valorMulta + valorJuros;
  const valorDesconto = invoicePaymentData.desconto ?? 0;
  const valorAReceberComDesconto = Math.max(0, valorAReceberComJurosMulta - valorDesconto);
  const effectivePaidAmount = (invoicePaymentData.paidAmount || 0) + (invoicePaymentData.creditDiscount || 0);

  const isPartialPayment = effectivePaidAmount > 0 && effectivePaidAmount < valorAReceberComDesconto;
  const isExcessPayment = effectivePaidAmount > valorAReceberComDesconto;
  const isExactPayment = effectivePaidAmount === valorAReceberComDesconto && valorAReceberComDesconto > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ py: 1, px: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Receipt fontSize="small" color="primary" />
            <Typography variant="subtitle1" fontWeight="bold">
              Receber Pagamento #{displayInvoice?.id ?? invoice?.id}
            </Typography>
          </Box>
          <Chip
            label={invoiceService.formatStatus((displayInvoice?.status ?? invoice?.status) || '').label}
            color={invoiceService.formatStatus((displayInvoice?.status ?? invoice?.status) || '').color}
            size="small"
            sx={{ height: 22, fontSize: '0.75rem' }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ py: 1, px: 2 }}>
        {loadingModal && !modalData ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          modalData && displayInvoice && (
            <Box display="flex" flexDirection="column" gap={1.5}>
              {/* Resumo Compacto da Fatura */}
              <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Fatura
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(displayInvoice.valor)}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Já Pago
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      {formatCurrency(totalPaid)}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Juros/Multa
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={(valorMulta + valorJuros) > 0 ? 'warning.main' : 'text.primary'}
                    >
                      {formatCurrency(valorMulta + valorJuros)}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      A Receber
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={valorAReceberComJurosMulta > 0 ? 'warning.main' : 'success.main'}
                    >
                      {formatCurrency(valorAReceberComJurosMulta)}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Sublinha de datas */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.75} pt={0.75} borderTop={1} borderColor="divider">
                  <Typography variant="caption" color="text.secondary">
                    Vencimento: <strong>{displayInvoice.data_vencimento ? invoiceService.formatarData(displayInvoice.data_vencimento) : '-'}</strong>
                  </Typography>
                </Box>
              </Paper>

              {/* Formulário de Campos */}
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Valor a Receber *"
                    fullWidth
                    value={getDisplayValue()}
                    onChange={handleValueChange}
                    placeholder="R$ 0,00"
                    size="small"
                    inputProps={{ inputMode: 'numeric' }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormaPgtoSelect
                    value={invoicePaymentData.paymentMethod}
                    onChange={(value) => setInvoicePaymentData(prev => ({ ...prev, paymentMethod: value }))}
                    label="Método de Pagamento *"
                    size="small"
                    showDescription={false}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Desconto"
                    fullWidth
                    value={getDescontoDisplayValue()}
                    onChange={handleDescontoChange}
                    placeholder="R$ 0,00"
                    size="small"
                    inputProps={{ inputMode: 'numeric' }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Motivo de Desconto</InputLabel>
                    <Select
                      value={invoicePaymentData.motivoDescontoId ?? ''}
                      onChange={(e) => setInvoicePaymentData(prev => ({ ...prev, motivoDescontoId: e.target.value ? Number(e.target.value) : undefined }))}
                      label="Motivo de Desconto"
                    >
                      <MenuItem value="">
                        <em>Nenhum</em>
                      </MenuItem>
                      {motivosDesconto.map((m) => (
                        <MenuItem key={m.value} value={m.value}>
                          {m.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Alerta de Desconto por Pontualidade Aplicado */}
                {(() => {
                  const currentAutoDesconto = (modalData?.invoice && modalData?.descontoAntecipadoConfig)
                    ? calculateDescontoAntecipado(modalData.invoice, invoicePaymentData.paymentDate || '', modalData.descontoAntecipadoConfig)
                    : 0;

                  const isAutoDescontoAplicado = currentAutoDesconto > 0 && (invoicePaymentData.desconto ?? 0) === currentAutoDesconto;

                  if (!isAutoDescontoAplicado || !modalData?.descontoAntecipadoConfig) return null;

                  return (
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ py: 0.25, px: 1, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                        <strong>Desconto Pontualidade:</strong> {formatCurrency(invoicePaymentData.desconto || 0)}
                        {modalData.descontoAntecipadoConfig.tipo === 'PERCENTAGE'
                          ? ` (${modalData.descontoAntecipadoConfig.valor}%)`
                          : ` (R$ ${modalData.descontoAntecipadoConfig.valor} fixo)`
                        }
                      </Alert>
                    </Grid>
                  );
                })()}

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Data e Hora do Pagamento *"
                    type="datetime-local"
                    fullWidth
                    value={invoicePaymentData.paymentDate}
                    onChange={(e) => {
                      const newPaymentDate = e.target.value;
                      if (modalData?.invoice && modalData?.descontoAntecipadoConfig) {
                        const autoDesconto = calculateDescontoAntecipado(modalData.invoice, newPaymentDate, modalData.descontoAntecipadoConfig);
                        const valorMulta = modalData.invoice.valor_multa ?? 0;
                        const valorJuros = modalData.invoice.valor_juros ?? 0;
                        const baseRemaining = modalData.paymentSummary.remainingAmount;
                        const valorAReceberComJurosMulta = baseRemaining + valorMulta + valorJuros;
                        const newAmountToPay = Math.max(0, valorAReceberComJurosMulta - (invoicePaymentData.creditDiscount ?? 0) - autoDesconto);

                        let autoMotivoId = invoicePaymentData.motivoDescontoId;
                        if (autoDesconto > 0 && !autoMotivoId && motivosDesconto.length > 0) {
                          const defaultMotivo = motivosDesconto.find(m => m.is_default);
                          const motivoAntecipacao = defaultMotivo || motivosDesconto.find(m =>
                            m.label.toLowerCase().includes('antecip') || m.label.toLowerCase().includes('pontual')
                          );
                          if (motivoAntecipacao) autoMotivoId = motivoAntecipacao.value;
                        } else if (autoDesconto === 0 && autoMotivoId) {
                          const defaultMotivo = motivosDesconto.find(m => m.is_default);
                          if (defaultMotivo && autoMotivoId === defaultMotivo.value) {
                            autoMotivoId = undefined;
                          }
                        }

                        setInvoicePaymentData(prev => ({
                          ...prev,
                          paymentDate: newPaymentDate,
                          desconto: autoDesconto,
                          motivoDescontoId: autoMotivoId,
                          paidAmount: newAmountToPay
                        }));
                      } else {
                        setInvoicePaymentData(prev => ({ ...prev, paymentDate: newPaymentDate }));
                      }
                    }}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* Seleção de Créditos Disponíveis */}
                {(modalData?.availableCredits?.length ?? 0) > 0 && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Créditos Disponíveis</InputLabel>
                      <Select
                        multiple
                        value={invoicePaymentData.selectedCredits || []}
                        onChange={(e) => handleCreditSelection(e.target.value as number[])}
                        input={<OutlinedInput label="Créditos Disponíveis" />}
                        renderValue={(selected) => {
                          const credits = modalData?.availableCredits ?? [];
                          const selectedCredits = credits.filter(credit =>
                            (selected as number[]).includes(credit.id)
                          );
                          const totalValue = selectedCredits.reduce((sum, credit) => sum + (Number(credit.saldo) || 0), 0);
                          return `${selectedCredits.length} crédito(s) (${formatCurrency(totalValue)})`;
                        }}
                        disabled={loadingModal}
                        size="small"
                      >
                        {(modalData?.availableCredits ?? []).map((credit) => (
                          <MenuItem key={credit.id} value={credit.id}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <Checkbox
                                checked={(invoicePaymentData.selectedCredits || []).includes(credit.id)}
                                tabIndex={-1}
                                disableRipple
                                size="small"
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={formatCreditOption(credit)}
                              secondary={`Tipo: ${clienteCreditoService.formatTipoCredito(credit.tipo)}`}
                              primaryTypographyProps={{ fontSize: '0.8rem' }}
                              secondaryTypographyProps={{ fontSize: '0.75rem' }}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {/* Linha Compacta: Comprovante e Observações */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Comprovante / ID Transação"
                    fullWidth
                    value={invoicePaymentData.transactionId || ''}
                    onChange={(e) => setInvoicePaymentData(prev => ({ ...prev, transactionId: e.target.value }))}
                    placeholder="Código PIX, comprovante..."
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Observações"
                    fullWidth
                    value={invoicePaymentData.notes || ''}
                    onChange={(e) => setInvoicePaymentData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Observações do pagamento"
                    size="small"
                  />
                </Grid>

                {/* Alertas de Status / Erros */}
                {errors.length > 0 && (
                  <Grid item xs={12}>
                    <Alert severity="error" sx={{ py: 0.25, px: 1, fontSize: '0.75rem' }}>
                      {errors.map((error, index) => (
                        <div key={index}>{error}</div>
                      ))}
                    </Alert>
                  </Grid>
                )}

                {isPartialPayment && (
                  <Grid item xs={12}>
                    <Alert severity="warning" sx={{ py: 0.25, px: 1, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                      <strong>Pagamento Parcial:</strong> {formatCurrency(effectivePaidAmount)} de {formatCurrency(valorAReceberComJurosMulta)}
                    </Alert>
                  </Grid>
                )}

                {isExcessPayment && (
                  <Grid item xs={12}>
                    <Alert severity="info" icon={<AccountBalanceWallet fontSize="small" />} sx={{ py: 0.25, px: 1, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                      <strong>Excedente:</strong> {formatCurrency(invoicePaymentData.excessAmount || 0)} será convertido em crédito.
                    </Alert>
                  </Grid>
                )}

                {isExactPayment && (
                  <Grid item xs={12}>
                    <Alert severity="success" sx={{ py: 0.25, px: 1, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                      <strong>Pagamento Completo:</strong> Fatura será quitada integralmente.
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          )
        )}
      </DialogContent>

      <DialogActions sx={{ py: 1, px: 2 }}>
        <Button onClick={handleClose} disabled={paymentLoading || loadingModal} size="small">
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!invoicePaymentData.paymentMethod || !invoicePaymentData.paidAmount || paymentLoading || loadingModal || !modalData}
          startIcon={paymentLoading ? undefined : <Receipt />}
          size="small"
        >
          {paymentLoading ? 'Processando...' : 'Confirmar Pagamento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePaymentForm;